import { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BasicTable,
  Button,
  ButtonLink,
  ContentBox,
  ContentPage,
  DescriptionList,
  ModalDefault,
  ModalForm,
  NoResults,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { hasPermission } from "@helpers/permissions";
import ProjectHoursTab from "@components/ProjectHoursTab";
import { DateText, DomainStatus, escapeHtml, usePageHeader } from "@components/ow";
import {
  CONTRACT_STATUS,
  ContractDetailSkeleton,
  DetailTabs,
  remunerationSummary,
} from "../../Common/ContractDetail";
import { focusFirstError } from "../../Common/FormPage";
import { ProjectFields, projectPayload, validateProject } from "./ProjectFields";

// Detalhe do projeto (modelo Detalhe do DS OneWork): abas Horas, Contratos e Dados,
// com as ações da página na linha das abas. Editar e excluir só para o ADM.

const LIST_HREF = "/c/projects";
const EDIT_PREFIX = "edit-project";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$gap || "20px"};
`;

const DataColumn = styled.div`
  max-width: 50%;
`;

/** Texto `YYYY-MM-DD` (sem a hora que a API às vezes manda) para os campos do editar. */
const isoDay = (value) => (value ? String(value).split("T")[0] : "");

function formValues(project) {
  return {
    name: project?.name || "",
    erpCode: project?.erp_code || "",
    startDate: isoDay(project?.start_date),
    endDate: isoDay(project?.end_date),
  };
}

export default function ProjectDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const projectService = useService("projects");
  const contractService = useService("contracts");
  const isAdmin = JSON.parse(localStorage.getItem("user_ow") || "{}")?.role === "ADM";
  // "Ver projetos" abre o projeto; as horas e a aprovação dependem dos itens de horas nele
  const canReadHours = hasPermission("project_hours:read", params.id);
  const canDecide = hasPermission("project_hours:approve", params.id) && hasPermission("project_hours:reject", params.id);
  const canReadContracts = hasPermission("contracts:read");

  const [project, setProject] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(canReadHours ? "horas" : canReadContracts ? "contratos" : "dados");

  // Editar
  const [editValues, setEditValues] = useState(null);
  const [editSubmitted, setEditSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  // Excluir
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const name = project?.name || "Projeto";
  usePageHeader({
    title: loading ? "Projeto" : name,
    breadcrumb: [
      { page: "Projetos", route: LIST_HREF },
      { page: loading ? "Projeto" : name, route: `${LIST_HREF}/${params.id}` },
    ],
  });

  useEffect(() => {
    fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  async function fetchProject() {
    try {
      // Contratos para o ADM ou com "Ver contratos"; as horas a aba busca
      const [projectResult, contractsResult] = await Promise.all([
        projectService.get(params.id),
        canReadContracts ? contractService.list(`scope=company&project_id=${params.id}&limit=9999`) : null,
      ]);
      setProject(projectResult.data);
      setContracts(contractsResult?.data?.list || []);
      setLoading(false);
    } catch (error) {
      console.error(error);
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        toast.error("Você não tem acesso a este projeto");
        navigate(LIST_HREF);
        return;
      }
      toast.error("Erro ao carregar dados do projeto");
      setLoading(false);
    }
  }

  function openEdit() {
    setEditValues(formValues(project));
    setEditSubmitted(false);
  }

  function closeEdit() {
    if (saving) return;
    setEditValues(null);
  }

  async function handleSave() {
    setEditSubmitted(true);
    const errors = validateProject(editValues, EDIT_PREFIX);
    if (Object.keys(errors).length) {
      focusFirstError(Object.keys(errors));
      return;
    }

    setSaving(true);
    // Em erro, o update do service devolve vazio
    const result = await projectService.update(projectPayload(editValues), params.id);
    setSaving(false);
    if (!result || result.error) {
      toast.error("Erro ao atualizar projeto");
      return;
    }

    toast.success("Projeto atualizado com sucesso");
    setEditValues(null);
    fetchProject();
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await projectService.delete(params.id);
      toast.success("Projeto excluído");
      navigate(LIST_HREF);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.msg || "Erro ao excluir projeto");
      setDeleting(false);
    }
  }

  if (loading) return <ContractDetailSkeleton />;

  const tabs = [];

  if (canReadHours) {
    tabs.push({
      value: "horas",
      label: "Horas",
      content: <ProjectHoursTab projectId={parseInt(params.id)} isAdmin canDecide={canDecide} startOnWaiting />,
    });
  }

  if (canReadContracts) {
    tabs.push({
      value: "contratos",
      label: "Contratos",
      content: <ContractsTab contracts={contracts} navigate={navigate} />,
    });
  }

  tabs.push({
    value: "dados",
    label: "Dados",
    content: (
      <DataColumn>
        <DescriptionList
          $title="Dados do projeto"
          $items={[
            { label: "Início", value: project?.start_date ? <DateText value={project.start_date} /> : null },
            { label: "Término", value: project?.end_date ? <DateText value={project.end_date} /> : "Sem término" },
            { label: "Código ERP", value: project?.erp_code || null },
          ]}
        />
      </DataColumn>
    ),
  });

  const actions = (
    <>
      {canReadHours ? (
        <ButtonLink
          $text={canDecide ? "Aprovar horas de todos os projetos" : "Horas de todos os projetos"}
          $action={() => navigate("/c/hours-approval")}
          $noWidth
        />
      ) : null}
      {isAdmin && project ? (
        <>
          {/* Quem aprova o projeto é definido na tela de permissões */}
          <Button
            $text="Aprovadores"
            $variant="outline"
            $noWidth
            $action={() => navigate(`/c/permissions?grupo=project_hours&projeto=${params.id}`)}
          />
          <Button $text="Editar projeto" $variant="outline" $noWidth $action={openEdit} />
          <Button $text="Excluir projeto" $variant="outline_danger" $noWidth $action={() => setShowDelete(true)} />
        </>
      ) : null}
    </>
  );

  const editErrors = editSubmitted && editValues ? validateProject(editValues, EDIT_PREFIX) : {};

  return (
    <ContentPage>
      <ContentBox>
        <DetailTabs tabs={tabs} value={activeTab} onChange={setActiveTab} actions={actions} />
      </ContentBox>

      {editValues && (
        <ModalForm
          $showModal={!!editValues}
          $setShowModal={closeEdit}
          $noClose={saving}
          $isLoading={saving}
          $size="medium"
          $title="Editar projeto"
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeEdit, variant: "outline", isDisabled: saving },
            { text: "Salvar alterações", action: handleSave, variant: "primary", isDisabled: saving },
          ]}
        >
          <Stack>
            <ProjectFields
              idPrefix={EDIT_PREFIX}
              inModal
              values={editValues}
              errors={editErrors}
              onChange={(field, value) => setEditValues((prev) => ({ ...prev, [field]: value }))}
            />
          </Stack>
        </ModalForm>
      )}

      {showDelete && (
        <ModalDefault
          $showModal={showDelete}
          $setShowModal={() => !deleting && setShowDelete(false)}
          $noClose={deleting}
          $size="medium"
          $title="Excluir projeto?"
          $contentBackground="N0"
          $footer={
            <>
              <Button $text="Cancelar" $variant="outline" $noWidth $isDisabled={deleting} $action={() => setShowDelete(false)} />
              <Button $text="Excluir projeto" $variant="danger" $noWidth $isLoading={deleting} $action={handleDelete} />
            </>
          }
        >
          <Text
            $text="O projeto <strong>%s</strong> será excluído e os prestadores saem dele. A exclusão não tem volta."
            $replaces={[escapeHtml(name)]}
            $variant="paragraph_small_medium"
            $color="N700"
            $colorEmphasis="N900"
          />
        </ModalDefault>
      )}
    </ContentPage>
  );
}

/** Contratos ligados ao projeto; o clique na linha abre o contrato. */
function ContractsTab({ contracts, navigate }) {
  const columns = [
    {
      id: "contractor",
      header: "Prestador",
      size: 320,
      cell: ({ row }) => {
        const contractor = row.original.contractor || {};
        const detail = contractor.company_name || contractor.email;
        return (
          <Stack $gap="2px">
            <span>{contractor.name || "—"}</span>
            {detail ? <Text $text={escapeHtml(detail)} $variant="caption_medium" $color="N500" /> : null}
          </Stack>
        );
      },
    },
    { id: "remuneration", header: "Remuneração", size: 260, cell: ({ row }) => remunerationSummary(row.original) },
    {
      id: "status",
      header: "Status",
      size: 140,
      cell: ({ row }) => <DomainStatus domain="contrato" value={CONTRACT_STATUS[row.original.status] || row.original.status} />,
    },
    {
      id: "start",
      header: "Início",
      size: 140,
      cell: ({ row }) => (row.original.start_date ? <DateText value={row.original.start_date} /> : "—"),
    },
  ];

  return (
    <BasicTable
      data={contracts}
      columns={columns}
      getRowId={(row) => String(row.id)}
      onRowClick={(contract) => navigate(`/c/contracts/${contract.id}`)}
      noResults={<NoResults notResultText="Nenhum contrato vinculado a este projeto" />}
    />
  );
}
